/**
 * Theme JavaScript for scroll reveal animations and gallery filtering
 * Uses IntersectionObserver for scroll-triggered animations
 * Implements interruptible FLIP animation for smooth filtering
 */

(function() {
  'use strict';
  
  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = window.matchMedia('(hover: hover)').matches;
  
  // Normalize function for tags and filter keys
  // Matches Liquid processing: downcase + strip (whitespace trimming)
  function normalize(str) {
    if (!str) {
      console.log('[normalize] Empty input, returning empty string');
      return '';
    }
    const original = String(str);
    // Convert to string, lowercase, and trim whitespace (matches Liquid downcase + strip)
    let normalized = original.toLowerCase().trim();
    // Remove any remaining whitespace characters
    normalized = normalized.replace(/\s+/g, ' ');
    normalized = normalized.trim();
    if (original !== normalized) {
      console.log('[normalize]', { original, normalized });
    }
    return normalized;
  }
  
  // Scroll reveal animations - reveal all immediately for instant loading
  // Reveal all items immediately instead of waiting for scroll
  document.querySelectorAll('.scroll-reveal').forEach(el => {
    el.classList.add('revealed');
  });
  
  // Gallery filtering with interruptible FLIP animation
  // Multiple rows supported: we bind events to the wrapper and treat all chips as one set.
  const filterWrap = document.querySelector('.dg-filterWrap');
  const galleryGrid = document.querySelector('[data-gallery-grid]');
  
  console.log('[Filter Init] Elements found:', {
    filterWrap: !!filterWrap,
    galleryGrid: !!galleryGrid,
    filterWrapElement: filterWrap,
    galleryGridElement: galleryGrid
  });
  
  if (filterWrap && galleryGrid) {
    // Select the outer wrapper elements (.gallery-grid__item) that have data-tags
    // NOT the inner .artwork-tile.dg-card elements which don't have data-tags
    const cards = Array.from(galleryGrid.querySelectorAll('.gallery-grid__item'));
    
    console.log('[Filter Init] Selected cards using .gallery-grid__item selector');
    
    // Verify cards have data-tags
    const cardsWithTags = cards.filter(card => card.dataset.tags);
    console.log('[Filter Init] Cards with data-tags:', cardsWithTags.length, 'out of', cards.length);
    
    if (cardsWithTags.length === 0 && cards.length > 0) {
      console.warn('[Filter Init] WARNING: Found cards but none have data-tags attribute!');
      console.log('[Filter Init] Sample card structure:', cards[0]);
      console.log('[Filter Init] Sample card dataset:', cards[0]?.dataset);
    }
    
    console.log('[Filter Init] Found cards:', cards.length);
    
    function refreshChipsCache(label = 'refreshChipsCache') {
      const allChips = Array.from(filterWrap.querySelectorAll('.dg-filterChip'));
      console.log(`[${label}] Found filter chips:`, allChips.length);
      allChips.forEach((chip, idx) => {
        console.log(`[${label}] Chip ${idx}:`, {
          text: chip.textContent.trim(),
          dataFilter: chip.dataset.filter,
          normalized: normalize(chip.dataset.filter || ''),
          isActive: chip.classList.contains('is-active')
        });
      });
      return allChips;
    }

    // Log all filter chips and their data attributes (initial)
    let allChips = refreshChipsCache('Filter Init');
    
    // Log sample card tags
    if (cards.length > 0) {
      console.log('[Filter Init] Sample card tags (first 3 cards):');
      cards.slice(0, 3).forEach((card, idx) => {
        const tagsRaw = card.dataset.tags || '';
        const tagsNormalized = tagsRaw.split('|').map(t => String(t).trim()).map(normalize).filter(t => t);
        console.log(`[Filter Init] Card ${idx} (${card.dataset.handle}):`, {
          tagsRaw: tagsRaw,
          tagsNormalized: tagsNormalized,
          tagsArray: tagsRaw.split('|')
        });
      });
    }
    
    // Track tag order for direction calculation
    let chipOrder = allChips.map(chip => normalize(chip.dataset.filter || ''));
    console.log('[Filter Init] Chip order (normalized):', chipOrder);
    
    // Multi-select state (normalized tags). Empty set => show all.
    let activeKeys = new Set();
    const transitionEndListeners = new Map();

    // Build a set of ALL tags that exist across rendered products.
    // Used to prune JSON-driven filter chips that don't exist on any product.
    function buildExistingTagSet() {
      const existing = new Set();
      let totalRaw = 0;
      cards.forEach((card) => {
        const raw = card.dataset.tags || '';
        if (!raw) return;
        raw.split('|').forEach((t) => {
          totalRaw++;
          const n = normalize(String(t).trim());
          if (n) existing.add(n);
        });
      });
      console.log('[Existing Tags] Built existing tag set:', {
        uniqueCount: existing.size,
        totalTagEntriesSeen: totalRaw,
        sample: Array.from(existing).slice(0, 30)
      });
      return existing;
    }

    let existingTagSet = buildExistingTagSet();
    
    // Get filters from URL (comma-separated): ?gallery=tag1,tag2
    function getFilterFromURL() {
      const params = new URLSearchParams(window.location.search);
      const filter = params.get('gallery');
      const normalizedList = filter
        ? filter
            .split(',')
            .map((s) => normalize(s))
            .filter(Boolean)
        : [];
      console.log('[getFilterFromURL]', {
        url: window.location.href,
        searchParams: window.location.search,
        rawFilter: filter,
        normalizedFilter: normalizedList
      });
      return normalizedList;
    }

    function setActiveKeys(nextKeys) {
      activeKeys = new Set(Array.isArray(nextKeys) ? nextKeys : []);
      console.log('[setActiveKeys] Active keys:', Array.from(activeKeys));

      const chips = Array.from(filterWrap.querySelectorAll('.dg-filterChip'));
      chips.forEach((chip) => {
        const k = normalize(chip.dataset.filter || chip.textContent || '');
        chip.classList.toggle('is-active', !!k && activeKeys.has(k));
      });
    }
    
    // Compute direction based on chip order
    function computeDirection(prevKey, nextKey) {
      const prevIdx = chipOrder.indexOf(prevKey);
      const nextIdx = chipOrder.indexOf(nextKey);
      if (prevIdx === -1 || nextIdx === -1) return 1;
      return nextIdx > prevIdx ? 1 : -1;
    }

    // Optional: load filter rows from a JSON asset you can edit in code:
    // assets/home-gallery-filters.json
    async function tryLoadFilterConfigJSON() {
      const url = filterWrap.getAttribute('data-filter-config-url');
      if (!url) {
        console.log('[Filter Config] No data-filter-config-url attribute found.');
        return false;
      }

      console.log('[Filter Config] Attempting to load JSON config:', url);
      try {
        const res = await fetch(url, { credentials: 'same-origin' });
        console.log('[Filter Config] Fetch status:', res.status, res.statusText);
        if (!res.ok) return false;
        const json = await res.json();
        console.log('[Filter Config] Loaded JSON:', json);

        const rows = Array.isArray(json?.rows) ? json.rows : null;
        if (!rows || rows.length === 0) {
          console.log('[Filter Config] No rows found in JSON.');
          return false;
        }

        // We want a SINGLE row for layout.
        const mainRow = filterWrap.querySelector('[data-filter-row-kind="main"]') || filterWrap.querySelector('[data-filter-row]');
        if (!mainRow) {
          console.warn('[Filter Config] No main filter row found to inject chips into.');
          return false;
        }

        // Remove any existing chips.
        const existingChips = Array.from(mainRow.querySelectorAll('.dg-filterChip'));
        console.log('[Filter Config] Existing chips in main row:', existingChips.length);
        existingChips.forEach((chip) => chip.remove());

        // Flatten rows -> one list, preserving order, de-duping by normalized form.
        const seen = new Set();
        const flattened = [];

        rows.forEach((row, rowIdx) => {
          if (!Array.isArray(row) || row.length === 0) {
            console.log(`[Filter Config] Skipping empty/non-array row ${rowIdx}:`, row);
            return;
          }
          row.forEach((tag, tagIdx) => {
            if (tag == null) return;
            const tagText = String(tag).trim();
            if (!tagText) return;
            if (tagText.startsWith('!')) return;

            const normalizedTag = normalize(tagText);
            if (!normalizedTag) return;

            // prune: only keep tags that exist on at least one product
            if (!existingTagSet.has(normalizedTag)) {
              console.log(`[Filter Config] Pruned non-existent tag (row ${rowIdx} idx ${tagIdx}):`, {
                tagText,
                normalizedTag,
                reason: 'Tag not found on any rendered product'
              });
              return;
            }

            if (seen.has(normalizedTag)) {
              console.log(`[Filter Config] De-duped tag (row ${rowIdx} idx ${tagIdx}):`, {
                tagText,
                normalizedTag
              });
              return;
            }

            seen.add(normalizedTag);
            flattened.push({ tagText, normalizedTag, rowIdx, tagIdx });
          });
        });

        console.log('[Filter Config] Flattened tags kept:', flattened.length, flattened.map(t => t.tagText));

        // Inject chips into the main row
        flattened.forEach((t) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'dg-filterChip';
          btn.dataset.filter = t.tagText;
          btn.textContent = t.tagText;
          mainRow.appendChild(btn);
        });

        // Refresh cached chips + order after DOM injection
        allChips = refreshChipsCache('Filter Config (post-inject)');
        chipOrder = allChips.map(chip => normalize(chip.dataset.filter || ''));
        console.log('[Filter Config] Updated chip order:', chipOrder);

        return true;
      } catch (err) {
        console.warn('[Filter Config] Failed to load/parse JSON config:', err);
        return false;
      }
    }
    
    // Get visible set of handles (OR across selected tags)
    function getVisibleSet(keySet) {
      const keys = keySet instanceof Set ? keySet : new Set();
      console.log('[getVisibleSet] Starting filter with keys:', Array.from(keys));
      const visible = new Set();
      
      // If no filters are selected, show all
      if (!keys || keys.size === 0) {
        console.log('[getVisibleSet] No filters selected, showing all cards');
        cards.forEach(card => {
          visible.add(card.dataset.handle);
        });
        console.log('[getVisibleSet] Returning', visible.size, 'visible cards');
        return visible;
      }
      
      // OR: show if any selected tag is present
      console.log('[getVisibleSet] Filtering by OR across:', Array.from(keys));
      let cardsChecked = 0;
      let cardsWithTags = 0;
      let cardsMatched = 0;
      
      cards.forEach((card, cardIdx) => {
        cardsChecked++;
        let cardTagsRaw = card.dataset.tags || '';
        const cardHandle = card.dataset.handle || `card-${cardIdx}`;
        
        if (!cardTagsRaw) {
          console.log(`[getVisibleSet] Card ${cardIdx} (${cardHandle}): No tags found`, {
            element: card,
            className: card.className,
            dataset: card.dataset,
            hasDataTags: 'tags' in card.dataset,
            dataTagsValue: card.dataset.tags
          });
          return; // Skip cards with no tags
        }
        
        cardsWithTags++;
        
        // Split by | and normalize each tag, filtering out empty strings
        const tagsArray = cardTagsRaw.split('|');
        const cardTags = tagsArray
          .map(t => String(t).trim()) // Convert to string and trim whitespace
          .map(normalize)
          .filter(t => t && t.length > 0); // Filter out empty strings
        
        // OR match: any selected key in cardTags
        const shouldShow = cardTags.some((t) => keys.has(t));
        
        console.log(`[getVisibleSet] Card ${cardIdx} (${cardHandle}):`, {
          tagsRaw: cardTagsRaw,
          tagsArray: tagsArray,
          tagsNormalized: cardTags,
          filterKeys: Array.from(keys),
          shouldShow: shouldShow,
          matchFound: shouldShow
        });
        
        if (shouldShow) {
          cardsMatched++;
          visible.add(card.dataset.handle);
        }
      });
      
      console.log('[getVisibleSet] Summary:', {
        totalCards: cardsChecked,
        cardsWithTags: cardsWithTags,
        cardsMatched: cardsMatched,
        visibleCount: visible.size,
        filterKeys: Array.from(keys)
      });
      
      return visible;
    }
    
    // Create rect map from current DOM state (handles mid-animation interrupts)
    function rectMap(els) {
      const m = new Map();
      els.forEach(el => {
        const handle = el.dataset.handle;
        if (handle) {
          const rect = el.getBoundingClientRect();
          const gridRect = galleryGrid.getBoundingClientRect();
          m.set(handle, {
            top: rect.top - gridRect.top,
            left: rect.left - gridRect.left,
            width: rect.width,
            height: rect.height
          });
        }
      });
      return m;
    }
    
    // Make element absolutely positioned at specific rect
    function makeAbsoluteAtRect(el, rect) {
      el.style.position = 'absolute';
      el.style.top = rect.top + 'px';
      el.style.left = rect.left + 'px';
      el.style.width = rect.width + 'px';
      el.style.height = rect.height + 'px';
      el.style.margin = '0';
    }
    
    // Reset absolute positioning
    function resetAbsolute(el) {
      el.style.position = '';
      el.style.top = '';
      el.style.left = '';
      el.style.width = '';
      el.style.height = '';
      el.style.margin = '';
    }
    
    // Cleanup transitionend listener
    function cleanupListener(el) {
      const listener = transitionEndListeners.get(el);
      if (listener) {
        el.removeEventListener('transitionend', listener);
        transitionEndListeners.delete(el);
      }
    }
    
    // Calculate distance-based duration for moves
    function getMoveDuration(dx, dy) {
      const distance = Math.sqrt(dx * dx + dy * dy);
      const duration = Math.max(450, Math.min(distance * 0.9, 1100));
      return duration;
    }
    
    // Apply current multi-select filters - instant update, no animations
    function animateFilterTransition() {
      console.log('[animateFilterTransition] Applying active keys:', Array.from(activeKeys));
      
      // Instant filter update - no animations
      console.log('[animateFilterTransition] Getting visible set...');
      const nextVisibleHandles = getVisibleSet(activeKeys);
      console.log('[animateFilterTransition] Visible handles:', Array.from(nextVisibleHandles));
      
      let shownCount = 0;
      let hiddenCount = 0;
      
      cards.forEach((card, cardIdx) => {
        const handle = card.dataset.handle;
        const shouldShow = nextVisibleHandles.has(handle);
        
        // Clean up any existing animation state
        cleanupListener(card);
        card.classList.remove('is-exiting', 'is-entering', 'is-moving', 'is-entering-prep', 'exit-left', 'exit-right');
        card.style.transform = '';
        card.style.transition = '';
        card.style.opacity = '';
        card.style.transformOrigin = '';
        resetAbsolute(card);
        
        // Show or hide instantly
        if (shouldShow) {
          shownCount++;
          card.classList.remove('is-hidden');
          card.style.display = '';
        } else {
          hiddenCount++;
          card.classList.add('is-hidden');
          card.style.display = 'none';
        }
      });
      
      console.log('[animateFilterTransition] Transition complete:', {
        filterKeys: Array.from(activeKeys),
        shown: shownCount,
        hidden: hiddenCount,
        total: cards.length
      });
    }
    
    // Update URL without reload (comma-separated normalized tags)
    function updateURLFromActiveKeys() {
      const url = new URL(window.location);
      if (!activeKeys || activeKeys.size === 0) {
        url.searchParams.delete('gallery');
      } else {
        url.searchParams.set('gallery', Array.from(activeKeys).join(','));
      }
      window.history.pushState({ filters: Array.from(activeKeys) }, '', url);
    }
    
    // Handle chip clicks
    filterWrap.addEventListener('click', function(e) {
      console.log('[Click Handler] Click event:', {
        target: e.target,
        currentTarget: e.currentTarget,
        tagName: e.target.tagName,
        className: e.target.className
      });
      
      const chip = e.target.closest('.dg-filterChip');
      console.log('[Click Handler] Closest chip:', chip);
      
      if (!chip) {
        console.log('[Click Handler] No chip found, ignoring click');
        return;
      }
      
      e.preventDefault();
      
      // Get the raw filter value from the data attribute
      // dataset API automatically decodes HTML entities
      const rawFilterKey = chip.dataset.filter || '';
      console.log('[Click Handler] Raw filter key from dataset:', rawFilterKey);
      console.log('[Click Handler] Full chip dataset:', chip.dataset);
      
      // Normalize it for comparison (same normalization used for card tags)
      const filterKey = normalize(rawFilterKey || chip.textContent || '');
      if (!filterKey) return;
      
      console.log('[Click Handler] Filter toggled:', {
        chipElement: chip,
        chipText: chip.textContent.trim(),
        rawFilterKey: rawFilterKey,
        normalizedFilterKey: filterKey,
        totalCards: cards.length,
        currentActiveKeys: Array.from(activeKeys)
      });
      
      // Toggle on/off
      if (activeKeys.has(filterKey)) {
        activeKeys.delete(filterKey);
      } else {
        activeKeys.add(filterKey);
      }
      // Sync DOM state + apply
      setActiveKeys(Array.from(activeKeys));
      animateFilterTransition();
      updateURLFromActiveKeys();
    });
    
    // Handle back/forward navigation - instant update
    window.addEventListener('popstate', function(e) {
      const keys = getFilterFromURL();
      setActiveKeys(keys);
      animateFilterTransition();
    });
    
    // Apply initial filter from URL
    console.log('[Init] Getting initial filters from URL...');
    const initialFilter = getFilterFromURL();
    console.log('[Init] Initial filters from URL:', initialFilter);
    setActiveKeys(initialFilter);
    
    // Apply the initial filter to show/hide cards
    console.log('[Init] Applying initial filter...');
    animateFilterTransition();
    console.log('[Init] Filter initialization complete');

    // Load JSON config (if present) and re-apply current filter (so page reflects new chips)
    tryLoadFilterConfigJSON().then((loaded) => {
      console.log('[Filter Config] Loaded?', loaded);
      if (loaded) {
        // Rebuild existing tag set in case DOM changed (defensive)
        existingTagSet = buildExistingTagSet();
        const current = getFilterFromURL();
        console.log('[Filter Config] Re-applying filter after config load:', current);
        setActiveKeys(current);
        animateFilterTransition();
      }
    });
  }

  // (Intentionally no cursor-follow glow on product tiles)
  
})();
